/**
 * The mailbox's signatures, edited as a list.
 *
 * It replaced a single textarea. One signature was enough while the mailbox
 * only ever wrote as one identity; the agency writes as itself and as the
 * product, and a reply that needs no sign-off at all is common enough that
 * "delete the signature, send, paste it back" was a real workflow.
 *
 * Its own file because the card it sits in is about credentials - hosts, ports,
 * passwords - and this is about wording. They change for different reasons and
 * the card was already long enough to scroll.
 *
 * ONE OF THEM IS THE DEFAULT and exactly one: the composer has to open with
 * something, and a list where none is marked would make that the first row by
 * accident of ordering rather than by anybody's decision.
 */
import { Plus, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { MailSignature } from "../../../../lib/mail";
import { Btn, IconBtn } from "../../../ui";
import { Field, ToggleRow } from "../panel/fields";

/** Ids only have to be unique within one mailbox, and they end up in a JSON
 *  setting rather than a table, so a short random one is enough. Kept to
 *  `[A-Za-z0-9_-]` because the API validates that shape. */
function newSignatureId(): string {
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function SignatureListField({
  rows,
  defaultId,
  onChange,
}: {
  rows: MailSignature[];
  defaultId: string;
  onChange: (rows: MailSignature[], defaultId: string) => void;
}) {
  const t = useT();
  // The stored default may be gone (deleted in this very session), so the mark
  // follows the same fallback the server uses: the first row.
  const activeDefault = rows.some((r) => r.id === defaultId) ? defaultId : rows[0]?.id ?? "";

  function patch(id: string, part: Partial<MailSignature>) {
    onChange(rows.map((r) => (r.id === id ? { ...r, ...part } : r)), activeDefault);
  }

  function remove(id: string) {
    const next = rows.filter((r) => r.id !== id);
    // Hand the mark on rather than leaving it pointing at nothing, so the list
    // that gets saved says what it means.
    onChange(next, activeDefault === id ? next[0]?.id ?? "" : activeDefault);
  }

  function add() {
    const row: MailSignature = { id: newSignatureId(), name: "", text: "" };
    onChange([...rows, row], activeDefault || row.id);
  }

  return (
    <Field label={t("settings.mail.signatures")} hint={t("settings.mail.signaturesHint")}>
      <div className="space-y-3">
        {rows.length === 0 && (
          <p className="crm-sub">{t("settings.mail.signatureEmpty")}</p>
        )}

        {rows.map((row) => (
          <div key={row.id} className="space-y-2 rounded-xl border border-[var(--crm-hair)] p-3">
            <div className="flex items-center gap-2">
              <input
                className="crm-inp min-w-0 flex-1"
                value={row.name}
                maxLength={80}
                placeholder={t("settings.mail.signatureNamePlaceholder")}
                onChange={(e) => patch(row.id, { name: e.target.value })}
              />
              <label
                className={cn(
                  "flex flex-none items-center gap-1.5 text-[11px]",
                  activeDefault === row.id ? "text-foreground" : "text-muted-foreground",
                )}
              >
                <input
                  type="radio"
                  checked={activeDefault === row.id}
                  onChange={() => onChange(rows, row.id)}
                />
                {t("settings.mail.signatureDefault")}
              </label>
              <IconBtn
                small
                title={t("settings.mail.signatureRemove")}
                aria-label={t("settings.mail.signatureRemove")}
                onClick={() => remove(row.id)}
              >
                <Trash2 className="size-3.5" />
              </IconBtn>
            </div>
            {/* `crm-area`, not a textarea inside `crm-inp`: that pill is a fixed
                52px flex row whose child rules only reach input and select, so a
                textarea in it keeps its browser border and folds to one line. */}
            <textarea
              className="crm-area min-h-[80px]"
              rows={3}
              maxLength={2000}
              value={row.text}
              placeholder={t("settings.mail.signatureText")}
              onChange={(e) => patch(row.id, { text: e.target.value })}
            />
            {/* The card itself is edited once, in the automatic letters'
                signature card on this page; here a signature only says
                whether it ends with it. */}
            <ToggleRow
              label={t("settings.mail.signatureCard")}
              hint={t("settings.mail.signatureCardHint")}
              checked={!!row.card}
              onChange={(v) => patch(row.id, { card: v })}
            />
          </div>
        ))}

        <Btn small onClick={add} type="button">
          <Plus className="size-3.5" />
          {t("settings.mail.signatureAdd")}
        </Btn>
      </div>
    </Field>
  );
}
